import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TitleService } from '@app/core/services/title.service';
import { Router } from '@angular/router';
import {
  AFTER_HOURS,
  AutoResponses,
  AutoResponseType,
  BUSINESS_HOURS,
  OUT_OF_OFFICE,
  SupportedLanguages,
  TwoWaySetupService,
} from '@app/two-way/services/two-way-setup.service';
import { ProductGroup } from '@app/core/models/client';
import { ConfigAutoResponsesDefaultMessages, TwoWayConfig } from '@app/core/models/two-way-config';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { NewFeedTransitionService } from '@app/core/services/new-feed-transition.service';
import { SessionService } from '@app/security/session.service';
import { Client } from '@app/core/models/client';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { FeatureService } from '@app/core/services/feature.service';
import { UnsavedChangesService } from '@app/core/services/unsaved-changes.service';
import { merge, Observable, Subject } from 'rxjs';
import { SetupBannerAutoresponseComponent } from '../setup-ooo-banner-autoresponse/setup-banner-autoresponse.component';
import { SpinnerComponent } from '../../../../shared/spinner/spinner.component';
import { NewGenericPhoneComponent } from '../../../../shared/new-generic-phone/new-generic-phone.component';
import { WireHeaderComponent } from '../../../../shared/wire-header/wire-header.component';
import { TwoWayPreviewComponent } from '../two-way-preview/two-way-preview.component';
import { NgClass } from '@angular/common';
import { ErrorDisplayComponent } from '../../../../shared/error-display/error-display.component';
import { BlackoutSelectComponent } from '../../../../shared/blackout-select/blackout-select.component';
import { TwoWayPreviewReactContainerComponent } from '../../../../shared/phone/two-way-preview-react-container/two-way-preview-react-container.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../../../shared/message-dialog/message-dialog.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { UnsavedChangesDialogComponent } from '../../../../shared/unsaved-changes-dialog/unsaved-changes-dialog.component';

export enum SelectedAutoResponse {
  DefaultAutoResponse = 'default-auto-response',
  AfterHoursAutoResponse = 'after-hours-auto-response',
  OutOfOfficeAutoResponse = 'out-of-office-auto-response',
}

@Component({
  selector: 'app-setup-auto-response',
  templateUrl: './setup-auto-response.component.html',
  styleUrls: ['./setup-auto-response.component.scss'],
  standalone: true,
  imports: [
    SetupBannerAutoresponseComponent,
    SpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    NewGenericPhoneComponent,
    WireHeaderComponent,
    TwoWayPreviewComponent,
    NgClass,
    ErrorDisplayComponent,
    BlackoutSelectComponent,
    TwoWayPreviewReactContainerComponent,
    MessageDialogComponent_1,
    MatFormFieldModule,
    MatSelectModule,
    UnsavedChangesDialogComponent,
  ],
})
export class SetupAutoResponseComponent implements OnInit, OnDestroy {
  @ViewChild('autoResponseErrorMessage', { static: true })
  autoResponseMessage: MessageDialogComponent;
  @ViewChild('unsavedChangesDialog', { static: true })
  unsavedChangesDialog: UnsavedChangesDialogComponent;
  @ViewChild('languageSelect')
  languageSelect: MatSelect;
  twoWayConfig: TwoWayConfig;
  autoResponseForm: UntypedFormGroup;
  defaultProductGroup: ProductGroup;
  currentClient: Client;
  isRMAutoResponseEnabled: boolean;
  selectedAutoResponse: SelectedAutoResponse;
  autoResponseMessageFeed: string;
  autoResponses: AutoResponses;
  isErrorAutoResponse: boolean;
  isErrorSupportedLanguages: boolean;
  isFeedEnabled: boolean;
  clientId: string;
  featureFlags = FeatureFlags;
  languageCode: string = 'en';
  supportedLanguages: SupportedLanguages[] = [];
  isFallbackAutoResponses = false;
  private readonly fallbackAutoResponseMessages: Record<AutoResponseType, string> = {
    businessHours: ConfigAutoResponsesDefaultMessages.defaultBusinessHoursAutoResponse,
    afterHours: ConfigAutoResponsesDefaultMessages.defaultAfterHoursAutoResponse,
    outOfOffice: ConfigAutoResponsesDefaultMessages.defaultOutOfOfficeAutoResponse,
  };
  private pendingLanguageCode: string | null = null;
  private pendingNavigation: Subject<boolean> | null = null; // Subject to track pending navigation when there are unsaved changes
  private readonly unsavedChangesHandler: () => Observable<boolean> | boolean;

  constructor(
    private titleService: TitleService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private twoWaySetupService: TwoWaySetupService,
    private newFeed: NewFeedTransitionService,
    private sessionService: SessionService,
    public featureService: FeatureService,
    private unsavedChangesService: UnsavedChangesService,
  ) {
    this.unsavedChangesHandler = this.canDeactivate.bind(this);
  }

  ngOnInit() {
    this.currentClient = this.sessionService.getCurrentUsersClient();
    this.clientId = this.currentClient.id;
    this.isFeedEnabled = this.newFeed.enabled;
    this.isRMAutoResponseEnabled = this.featureService.checkFlag(
      this.featureFlags.rm_auto_response,
    );
    if (this.isTranslationsEnabled) {
      this.unsavedChangesService.register(this.unsavedChangesHandler);
    }
    this.titleService.activate(
      this.currentClient.feed_enabled
        ? 'Relay Messenger Setup'
        : 'Two Way Messaging Setup',
      'Auto Responses',
    );

    // Display the auto response msgs from twoWayConfig if RMAutoResponseEnabled flag is not enabled or feed is not enabled
    if (!this.isRMAutoResponseEnabled || !this.isFeedEnabled) {
      this.twoWaySetupService.getTwoWayConfig().subscribe((twoWayConfig) => {
        this.twoWayConfig = twoWayConfig;
        this.initAutoResponseForm();

        if (!twoWayConfig.after_hours_blackout_window) {
          this.twoWaySetupService.getClient().subscribe((client) => {
            this.twoWayConfig.after_hours_blackout_window =
              client.blackout_window;
          });
        }
      });
    }

    this.twoWaySetupService
      .getDefaultProductGroup()
      .subscribe((defaultProductGroup) => {
        this.defaultProductGroup = defaultProductGroup;
      });

    if (this.isRMAutoResponseEnabled && this.isFeedEnabled) {
      if (this.isTranslationsEnabled) {
        this.twoWaySetupService.getSupportedLanguages(this.currentClient.id).subscribe({
          next: (supportedLanguages) => {
            this.supportedLanguages = supportedLanguages;
            this.isErrorSupportedLanguages = false;
          },
          error: () => {
            this.isErrorSupportedLanguages = true;
            this.supportedLanguages = [
              { code: 'en', name: 'English' },
            ]
          },
        });
        this.twoWaySetupService.getAutoResponsesWithLanguages(this.currentClient.id, this.languageCode).subscribe({
          next: ({ autoResponses, isFallback }) => {
            this.autoResponses = autoResponses;
            this.isFallbackAutoResponses = isFallback;
            this.autoResponseMessageFeed = this.getAutoResponseDisplayMessage(BUSINESS_HOURS);
            this.initAutoResponseForm();
            this.isErrorAutoResponse = false;
          },
          error: () => {
            this.isErrorAutoResponse = true;
          },
        });
      } else {
        this.twoWaySetupService
          .getAutoResponses(this.currentClient.id)
          .subscribe({
            next: (autoResponses) => {
              this.autoResponses = autoResponses;
              this.autoResponseMessageFeed =
                this.autoResponses.businessHours.message;
              this.initAutoResponseForm();
            },
            error: () => {
              this.isErrorAutoResponse = true;
            },
          });
      }
      this.selectedAutoResponse = SelectedAutoResponse.DefaultAutoResponse;
    }
  }

  ngOnDestroy() {
    if (this.isTranslationsEnabled) {
      this.unsavedChangesService.unregister(this.unsavedChangesHandler);
    }
    this.titleService.deactivate();
  }

  get defaultAutoResponseCtl(): AbstractControl {
    return this.autoResponseForm.get('default_auto_response');
  }

  get afterHoursAutoResponseCtl(): AbstractControl {
    return this.autoResponseForm.get('after_hours_auto_response');
  }

  get outOfOfficeAutoResponseCtl(): AbstractControl {
    return this.autoResponseForm.get('out_of_office_auto_response');
  }

  /**
   * Determines if the "RM Translations with Languages" is enabled for the user
   */
  get rmTranslationsWithLanguagesFlagOn(): boolean {
    return this.featureService.checkFlag(this.featureFlags.rm_multi_lingual);
  }

  get isTranslationsEnabled(): boolean {
    return (
      this.rmTranslationsWithLanguagesFlagOn &&
      !!this.currentClient?.two_way_config?.translations_enabled
    );
  }

  get currentLanguageLabel(): string | null {
    if (this.languageCode === 'en') {
      return null;
    }
    return (
      this.supportedLanguages.find((language) => language.code === this.languageCode)
        ?.name ?? null
    );
  }

  setSelectedAutoResponse(inputName: SelectedAutoResponse) {
    // If the user is switching auto response types, reveal validation errors if the current feed message is empty
    if (inputName !== this.selectedAutoResponse) {
      this.revealValidationIfEmpty(this.selectedAutoResponse);
    }
    this.selectedAutoResponse = inputName;
    if (inputName === SelectedAutoResponse.OutOfOfficeAutoResponse) {
      this.revealValidationIfEmpty(SelectedAutoResponse.AfterHoursAutoResponse);
    }
    // Set the auto response message feed based on the selected auto response
    if (inputName === SelectedAutoResponse.OutOfOfficeAutoResponse) {
      this.autoResponseMessageFeed = this.getAutoResponseDisplayMessage(OUT_OF_OFFICE);
    } else if (inputName === SelectedAutoResponse.AfterHoursAutoResponse) {
      this.autoResponseMessageFeed = this.getAutoResponseDisplayMessage(AFTER_HOURS);
    } else {
      this.autoResponseMessageFeed = this.getAutoResponseDisplayMessage(BUSINESS_HOURS);
    }
  }
  get headerColor() {
    return this.defaultProductGroup && this.defaultProductGroup?.branding?.color
      ? this.defaultProductGroup.branding.color
      : '#231F20';
  }
  isLoading(): boolean {
    return this.autoResponseForm === undefined;
  }

  saveAndContinue() {
    if (!this.autoResponseForm.valid) {
      return;
    }
    if (this.isRMAutoResponseEnabled && this.isFeedEnabled) {
      const autoresponsesBody = {
        autoResponses: {
          ...this.autoResponses,
        },
      };
      this.twoWaySetupService
        .saveAutoResponses(this.currentClient.id, autoresponsesBody)
        .subscribe({
          next: () => {
            this.router.navigateByUrl(`/two-way/setup/business-hours`);
          },
          error: () => {
            this.autoResponseMessage.showMessage(
              `We are sorry! There was an error saving the changes. Please try again`,
            );
          },
        });
    } else {
      this.updateConfigWithFormData();
      this.twoWaySetupService.saveTwoWayConfig(this.twoWayConfig).subscribe(
        (response) => {
          this.router.navigateByUrl(
            `/two-way/setup/${this.newFeed.enabled ? 'notification' : 'sms'
            }-ping`,
          );
        },
        (error) => {
          this.autoResponseMessage.showMessage(
            `We're sorry, there was an error saving your information.`,
          );
        },
      );
    }
  }

  /* Multilingual support */
  saveWithoutNavigation() {
    if (!this.autoResponseForm.valid) {
      return;
    }
    this.saveAutoResponsesWithLanguages(
      () => {
        this.autoResponseMessage.showMessage(`Your changes have been saved.`);
      },
      undefined,
      false,
    );
  }

  private updateConfigWithFormData() {
    const formData = this.autoResponseForm.getRawValue();
    this.twoWayConfig.default_auto_response = formData['default_auto_response'];
    this.twoWayConfig.after_hours_auto_response =
      formData['after_hours_auto_response'];
  }

  private initAutoResponseForm() {
    this.autoResponseForm = this.formBuilder.group(
      this.isRMAutoResponseEnabled && this.isFeedEnabled
        ? {
          default_auto_response: [
            this.autoResponses.businessHours.message,
            Validators.required,
          ],
          after_hours_auto_response: [
            this.autoResponses.afterHours.message,
            Validators.required,
          ],
          out_of_office_auto_response: [
            this.autoResponses.outOfOffice.message,
          ],
        }
        : {
          default_auto_response: [
            this.twoWayConfig.default_auto_response,
            Validators.required,
          ],
          after_hours_auto_response: [
            this.twoWayConfig.after_hours_auto_response,
            Validators.required,
          ],
        },
    );

    if (this.isRMAutoResponseEnabled && this.isFeedEnabled) {
      merge(
        this.defaultAutoResponseCtl.valueChanges,
        this.afterHoursAutoResponseCtl.valueChanges,
        this.outOfOfficeAutoResponseCtl.valueChanges,
      ).subscribe(() => {
        this.autoResponses = {
          businessHours: {
            message: this.defaultAutoResponseCtl.value,
          },
          afterHours: {
            message: this.afterHoursAutoResponseCtl.value,
          },
          outOfOffice: {
            message: this.outOfOfficeAutoResponseCtl.value,
          },
        };
      });
      // listen for changes in the auto response message feed
      this.defaultAutoResponseCtl.valueChanges.subscribe(() => {
        this.updatePreviewIfSelected(BUSINESS_HOURS, SelectedAutoResponse.DefaultAutoResponse);
      });
      this.afterHoursAutoResponseCtl.valueChanges.subscribe(() => {
        this.updatePreviewIfSelected(AFTER_HOURS, SelectedAutoResponse.AfterHoursAutoResponse);
      });
      this.outOfOfficeAutoResponseCtl.valueChanges.subscribe(() => {
        this.updatePreviewIfSelected(OUT_OF_OFFICE, SelectedAutoResponse.OutOfOfficeAutoResponse);
      });
    }
  }

  onLanguageChange(languageCode: string) {
    if (!this.isTranslationsEnabled || languageCode === this.languageCode) {
      return;
    }

    if (this.autoResponseForm?.dirty) {
      this.pendingLanguageCode = languageCode;
      this.languageSelect?.writeValue(this.languageCode);
      this.languageSelect?.close();
      this.openUnsavedChangesDialog();
      return;
    }

    this.switchLanguage(languageCode);
  }

  /* 
    Guard to prevent navigation away from the SetupAutoResponseComponent
    if there are unsaved changes.
  */
  canDeactivate(): Observable<boolean> | boolean {
    if (this.unsavedChangesService.consumeAllowNextNavigation()) {
      return true;
    }
    if (!this.isTranslationsEnabled) {
      return true;
    }
    if (!this.isRMAutoResponseEnabled || !this.isFeedEnabled) {
      return true;
    }
    if (!this.autoResponseForm || !this.hasUnsavedAutoResponseChanges()) {
      return true;
    }
    if (this.pendingNavigation) {
      return this.pendingNavigation.asObservable();
    }
    this.pendingNavigation = new Subject<boolean>();
    this.pendingLanguageCode = null;
    this.openUnsavedChangesDialog();
    return this.pendingNavigation.asObservable();
  }

  onUnsavedChangesSave() {
    if (this.pendingNavigation) {
      if (!this.autoResponseForm.valid) {
        return;
      }
      this.saveAutoResponsesWithLanguages(() => {
        this.resolvePendingNavigation(true);
      }, () => {
        this.resolvePendingNavigation(false);
      });
      return;
    }

    if (!this.pendingLanguageCode) {
      return;
    }
    const nextLanguage = this.pendingLanguageCode;
    this.pendingLanguageCode = null;

    this.saveAutoResponsesWithLanguages(() => {
      this.switchLanguage(nextLanguage);
    }, undefined, true);
  }

  onUnsavedChangesDiscard() {
    if (this.pendingNavigation) {
      this.resolvePendingNavigation(true);
      return;
    }
    if (!this.pendingLanguageCode) {
      return;
    }
    const nextLanguage = this.pendingLanguageCode;
    this.pendingLanguageCode = null;
    this.switchLanguage(nextLanguage);
  }

  onUnsavedChangesDismiss() {
    if (this.pendingNavigation) {
      this.resolvePendingNavigation(false);
    }
    this.pendingLanguageCode = null;
  }

  private openUnsavedChangesDialog() {
    const title = 'Changes not saved'
    const messageBody =
      'The changes you have made to auto responses have not been saved.\n' +
      'Would you like to save before leaving this page?';
    this.unsavedChangesDialog.open(title, messageBody);
  }

  private switchLanguage(languageCode: string) {
    this.languageCode = languageCode;
    this.twoWaySetupService.getAutoResponsesWithLanguages(this.currentClient.id, this.languageCode).subscribe({
      next: ({ autoResponses, isFallback }) => {
        this.autoResponses = autoResponses;
        this.isFallbackAutoResponses = isFallback;
        this.updateFormValuesFromAutoResponses();
        this.resetFormControlStates();
        // Reset selection to default when language changes
        this.selectedAutoResponse = SelectedAutoResponse.DefaultAutoResponse;
        this.autoResponseMessageFeed = this.getAutoResponseDisplayMessage(BUSINESS_HOURS);
        this.isErrorAutoResponse = false;
      },
      error: () => {
        this.isErrorAutoResponse = true;
      },
    });
  }

  get isFallbackPendingUpdates(): boolean {
    if (!this.isFallbackAutoResponses || !this.autoResponseForm) {
      return false;
    }
    return !this.areFallbackMessagesFilled();
  }

  private areFallbackMessagesFilled(): boolean {
    const controls = [
      this.defaultAutoResponseCtl,
      this.afterHoursAutoResponseCtl,
    ];
    return controls.every((control) => {
      const value = control?.value;
      return this.isNonEmptyString(value);
    });
  }

  private updateFormValuesFromAutoResponses() {
    if (!this.autoResponseForm) {
      return;
    }
    this.autoResponseForm.patchValue({
      default_auto_response: this.autoResponses.businessHours.message,
      after_hours_auto_response: this.autoResponses.afterHours.message,
      out_of_office_auto_response: this.autoResponses.outOfOffice.message,
    });
  }

  isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  getAutoResponsePlaceholder(
    type: AutoResponseType,
  ): string {
    if (!this.isTranslationsEnabled) {
      return '';
    }
    const value = this.getAutoResponseValueForPlaceholder(type);
    if (this.isNonEmptyString(value)) {
      return '';
    }
    return this.fallbackAutoResponseMessages[type] ?? '';
  }

  private getAutoResponseDisplayMessage(
    type: AutoResponseType,
  ): string {
    const value = this.getAutoResponseValueForPlaceholder(type);
    if (this.isNonEmptyString(value)) {
      return value;
    }
    if (!this.isTranslationsEnabled) {
      return value ?? '';
    }
    return this.fallbackAutoResponseMessages[type] ?? '';
  }

  private updatePreviewIfSelected(
    type: AutoResponseType,
    selection: SelectedAutoResponse,
  ) {
    if (this.selectedAutoResponse !== selection) {
      return;
    }
    this.autoResponseMessageFeed = this.getAutoResponseDisplayMessage(type);
  }

  private getAutoResponseValueForPlaceholder(
    type: AutoResponseType,
  ): string {
    // Use form control values once initialized; otherwise fall back to fetched autoResponses during load
    if (this.autoResponseForm) {
      if (type === BUSINESS_HOURS) {
        return this.defaultAutoResponseCtl?.value ?? '';
      }
      if (type === AFTER_HOURS) {
        return this.afterHoursAutoResponseCtl?.value ?? '';
      }
      return this.outOfOfficeAutoResponseCtl?.value ?? '';
    }
    if (type === BUSINESS_HOURS) {
      return this.autoResponses?.businessHours?.message ?? '';
    }
    if (type === AFTER_HOURS) {
      return this.autoResponses?.afterHours?.message ?? '';
    }
    return this.autoResponses?.outOfOffice?.message ?? '';
  }

  private resetFormControlStates() {
    const controls = [
      this.defaultAutoResponseCtl,
      this.afterHoursAutoResponseCtl,
      this.outOfOfficeAutoResponseCtl,
    ];
    controls.forEach((control) => {
      control?.markAsPristine({ onlySelf: true });
      control?.markAsUntouched({ onlySelf: true });
      control?.updateValueAndValidity({ onlySelf: true });
    });
    this.autoResponseForm?.markAsPristine({ onlySelf: true });
    this.autoResponseForm?.markAsUntouched({ onlySelf: true });
    this.autoResponseForm?.updateValueAndValidity({ onlySelf: true });
  }

  private revealValidationIfEmpty(selection: SelectedAutoResponse) {
    if (!selection) {
      return;
    }
    const control = this.getControlForSelection(selection);
    if (!control) {
      return;
    }
    const value = control.value;
    if (!this.isNonEmptyString(value)) {
      control.markAsTouched({ onlySelf: true });
      control.markAsDirty({ onlySelf: true });
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  private getControlForSelection(selection: SelectedAutoResponse): AbstractControl {
    switch (selection) {
      case SelectedAutoResponse.AfterHoursAutoResponse:
        return this.afterHoursAutoResponseCtl;
      case SelectedAutoResponse.OutOfOfficeAutoResponse:
        return this.outOfOfficeAutoResponseCtl;
      default:
        return this.defaultAutoResponseCtl;
    }
  }

  private hasUnsavedAutoResponseChanges(): boolean {
    if (!this.isTranslationsEnabled) {
      return false;
    }
    return !!(
      this.defaultAutoResponseCtl?.dirty ||
      this.afterHoursAutoResponseCtl?.dirty
    );
  }

  private resolvePendingNavigation(allow: boolean) {
    if (!this.pendingNavigation) {
      return;
    }
    this.pendingNavigation.next(allow);
    this.pendingNavigation.complete();
    this.pendingNavigation = null;
  }

  private saveAutoResponsesWithLanguages(
    onSuccess: () => void,
    onError?: () => void,
    showSuccessMessage = false,
  ) {
    const autoResponsesBody = {
      autoResponses: {
        ...this.autoResponses,
      },
    };
    this.twoWaySetupService
      .saveAutoResponsesWithLanguages(this.currentClient.id, autoResponsesBody, this.languageCode)
      .subscribe({
        next: () => {
          this.resetFormControlStates();
          if (showSuccessMessage) {
            this.autoResponseMessage.showMessage(`Your changes have been saved.`);
          }
          onSuccess();
        },
        error: () => {
          this.autoResponseMessage.showMessage(
            `We are sorry! There was an error saving the changes. Please try again`,
          );
          onError?.();
        },
      });
  }
}
